import { c as _c } from "react/compiler-runtime";
import { ActionIcon, SegmentedControl, SegmentedControlProps, TextInput, TextInputProps, Tooltip, useCombobox, Combobox as Combo, CheckIcon, Input, Alert, Switch, Button, Menu, ActionIconProps } from '@mantine/core';
import { Fragment, ReactNode, useCallback, useState } from 'react';
import { getHotkeyHandler, upperFirst } from '@mantine/hooks';
import { HiCalendar, HiCheckCircle, HiClipboardCopy, HiDotsVertical, HiExclamation, HiSelector, HiTrash } from 'react-icons/hi';
import { omit, pick } from 'remeda';
import { RichText } from 'components/form/RichText';
import { Bounds, Fmt, QuestionBounds, QuestionCfg, QuestionCfgCommon, questionAmountTypeMd, questionForecastMd, numberFormatTable, isDueStatus, type QuestionAttachments } from 'shared/forecast';
import { compactObj, entries, isNullOrEmpty, mapEntries } from 'shared/collection';
import { Rnge } from 'shared/range';
import { Expandable, WrapIf, Markdown } from 'shared/ui';
import { match } from 'ts-pattern';
import { MdEdit } from 'react-icons/md';
import { dbFormSubset, DbFormSubset, useDbForm } from 'components/hooks/useDbForm';
import { IconContext } from 'react-icons';
import { TiSortNumerically } from 'react-icons/ti';
import { InfoLabel, InfoTip } from 'components/Info';
import { tourTipProps } from 'components/portal/tourTypes';
import { StringNumberInput } from 'components/form/StringNumberInput';
import { DateInputMillis } from './ForecastLogisticEdit';
import Link from 'next/link';
import { useQueryString } from 'utils/useQueryString';
import { zQuestionQuery } from './questionComponentTypes';
import { compactString } from 'shared/text';
import { twMerge } from 'tailwind-merge';
import { useQuestionForm, useQuestionStats } from './useQuestions';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { showError } from 'components/notify';
import { usePortal } from 'components/portal/portal';
import { useTourContext } from 'components/portal/Tour';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { questionStatusMd } from 'shared/forecast/questionStatus';
import { DueButton, type DueCfg } from './Due';
import { ParticipationStatus, TaskedStatus } from './Participation';
import { AttachmentButton, QuestionAttachmentList, attachmentItems } from './QuestionAttachment';
import { Owner } from './QuestionOwner';
import { QuestionStatusButton } from './QuestionStatus';
import { QuestionTileForecastDates } from './QuestionTile';
import { useParticipation } from './participation';
import { useMyForecastProgress } from './progress';
import { dupeQuestion } from './dupe';
import { useMutation } from '@tanstack/react-query';
export const QuestionForm = ({
  question_id
}: {
  question_id: string;
}) => {
  const {
    profile
  } = usePortal();
  const {
    form,
    readonly,
    question,
    crud,
    isOwner
  } = useQuestionForm({
    question_id,
    pick: q => pick(q, ['tasked', 'taskedSlots', 'title', 'subTitle', 'description', 'status'])
  });
  const questionStats = useQuestionStats({
    question_id
  });
  const {
    values
  } = form;
  if (!values) return <></>;
  const participation = useParticipation({
    question_id,
    cfg: question?.cfg
  });
  const progress = useMyForecastProgress({
    question_id,
    pushDownQuestionFilter: true
  });
  const tour = useTourContext();
  const {
    workspace: {
      advancedUI
    }
  } = usePortal();
  const attachmentForm = dbFormSubset<QuestionAttachments>(form, 'attachments');
  const router = useRouter();
  const dupeMute = useMutation({
    mutationFn: async ({
      copy_forecasts
    }: {
      copy_forecasts: boolean;
    }) => dupeQuestion({
      type: 'question',
      copy_forecasts,
      profile,
      id: question_id
    }).then(r => {
      if (!r.ok) throw new Error(r.message);
      return r.value;
    }),
    onSuccess: newId => {
      showNotification({
        title: 'Question duplicated',
        message: 'Navigating to the new question'
      });
      router.push(`/question/${newId}`);
    },
    onError: error => {
      showError(error, 'Error duplicating question');
    }
  });
  const deleteMute = useMutation({
    mutationFn: async () => {
      await crud.update({
        id: question_id,
        deleted: true,
        updated_at: DateTime.utc().toISO()
      });
      router.push(`/?workspace=${profile.workspace_id}`);
    },
    onError: error_0 => {
      showError(error_0, 'Error deleting question');
    }
  });
  return <form className='flex max-w-[80em] flex-col items-start gap-2' onKeyDown={getHotkeyHandler([['Enter', e => e.preventDefault()]])} onSubmit={() => {}}>
      {values ? <>
          <div className='max-w-text w-full space-y-1 relative'>
            <QuestionTitleInput value={values.title} readonly={readonly} typeLabel='question' {...form.getInputProps('title')} topRight={!readonly && <div className='flex items-center gap-0 bg-[var(--mantine-color-body)]'>
                    {/* {!readonly && (
                      <SaveStatusCircle
                        className='m-2'
                        isDirty={form.isDirty}
                        disabled={!questionValidForSave(values)}
                      />
                     )} */}
                    <Menu width={230}>
                      <Menu.Target>
                        <ActionIcon tabIndex={1}>
                          <HiDotsVertical size='1.3em' />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<HiTrash />} onClick={() => {
                openConfirmModal({
                  title: `Delete question?`,
                  centered: true,
                  children: <span>
                                  Are you sure you want to delete the question: <b>{values.title}</b>
                                </span>,
                  labels: {
                    confirm: `Delete question`,
                    cancel: 'Cancel'
                  },
                  confirmProps: {
                    color: 'red',
                    variant: 'filled'
                  },
                  onConfirm: () => deleteMute.mutate()
                });
              }}>
                          Delete
                        </Menu.Item>
                        <Menu.Item leftSection={<HiClipboardCopy />} onClick={() => dupeMute.mutate({
                copy_forecasts: false
              })}>
                          Duplicate
                        </Menu.Item>
                        <Menu.Item leftSection={<HiClipboardCopy />} onClick={() => dupeMute.mutate({
                copy_forecasts: true
              })}>
                          Duplicate with forecasts
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </div>} />
            <QuestionSubtitleInput value={values.subTitle} readonly={readonly} {...form.getInputProps('subTitle')} />
            {question?.cfg && <>
                <div className='flex flex-wrap items-center gap-2'>
                  {participation && advancedUI && <ParticipationStatus participation={participation} />}
                  <QuestionStatusButton mds={questionStatusMd} value={values.status} onDropdownOpen={() => {
              if (tour.tip?.id == 'question-status-editable') tour.next('ok');
            }} onChange={status => {
              form.setValues({
                status,
                [`statusAt.${status}`]: DateTime.utc().toISO()
              });
            }} readonly={readonly} {...tourTipProps('question-status-editable', isOwner)} />
                  {isDueStatus(values.status) && <DueButton status={values.status} form={dbFormSubset<DueCfg>(form, `due.${values.status}`)} readonly={readonly} />}
                  {questionStats && question.cfg && <QuestionTileForecastDates {...pick(questionStats, ['forecasters_total', 'forecasts_from', 'forecasts_to'])} created_at={question.created_at} />}
                  <Owner readonly={readonly} value={values.owner} created_by={question.created_by} onChange={id => form.setFieldValue('owner', id)} />
                  {advancedUI && progress && <TaskedStatus isTasked={participation.isTasked} progress={progress} />}
                  {advancedUI && <AttachmentButton form={attachmentForm} />}
                </div>
                <QuestionAttachmentList attachments={attachmentItems(attachmentForm.values)} setFieldValue={attachmentForm.setFieldValue} className='flex-row flex-wrap' />
              </>}
          </div>
        </> : <></>}
    </form>;
};

/** Input component for question titles with consistent styling */
export const QuestionTitleInput = t0 => {
  const $ = _c(21);
  let autoFocus;
  let onChange;
  let props;
  let readonly;
  let topRight;
  let typeLabel;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      readonly,
      typeLabel,
      autoFocus,
      topRight,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = autoFocus;
    $[2] = onChange;
    $[3] = props;
    $[4] = readonly;
    $[5] = topRight;
    $[6] = typeLabel;
    $[7] = value;
  } else {
    autoFocus = $[1];
    onChange = $[2];
    props = $[3];
    readonly = $[4];
    topRight = $[5];
    typeLabel = $[6];
    value = $[7];
  }
  const t1 = topRight != null;
  let t2;
  if ($[8] !== topRight) {
    t2 = c => <div className="flex justify-between"><div className="grow">{c}</div>{topRight}</div>;
    $[8] = topRight;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== autoFocus || $[11] !== onChange || $[12] !== props || $[13] !== readonly || $[14] !== typeLabel || $[15] !== value) {
    t3 = readonly ? <h3>{value}</h3> : <TextInput variant="title" placeholder={`Enter a ${typeLabel ?? "question"} title`} autoFocus={autoFocus ?? isNullOrEmpty(value)} readOnly={readonly} value={value} onChange={e => onChange?.(e.currentTarget.value)} data-test-id="question-title" {...props} />;
    $[10] = autoFocus;
    $[11] = onChange;
    $[12] = props;
    $[13] = readonly;
    $[14] = typeLabel;
    $[15] = value;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t4 = <WrapIf if={t1} wrap={t2}>{t3}</WrapIf>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
};

/** Input component for question subtitles with consistent styling */
export const QuestionSubtitleInput = t0 => {
  const $ = _c(14);
  let classNameRaw;
  let onChange;
  let props;
  let readonly;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      readonly,
      className: classNameRaw,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = classNameRaw;
    $[2] = onChange;
    $[3] = props;
    $[4] = readonly;
    $[5] = value;
  } else {
    classNameRaw = $[1];
    onChange = $[2];
    props = $[3];
    readonly = $[4];
    value = $[5];
  }
  let t1;
  if ($[6] !== classNameRaw) {
    t1 = twMerge("", classNameRaw);
    $[6] = classNameRaw;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const className = t1;
  let t2;
  if ($[8] !== className || $[9] !== onChange || $[10] !== props || $[11] !== readonly || $[12] !== value) {
    t2 = readonly ? <p className={className}>{value}</p> : <TextInput className={className} variant="subtitle" placeholder="Subtitle (optional - important clarifications to the question)" readOnly={readonly} value={value} onChange={e => onChange?.(e.currentTarget.value)} data-test-id="question-subtitle" {...props} />;
    $[8] = className;
    $[9] = onChange;
    $[10] = props;
    $[11] = readonly;
    $[12] = value;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
};
export const EditActionIcon = t0 => {
  const $ = _c(25);
  let className;
  let editLabel;
  let editing;
  let props;
  let setEditing;
  let t1;
  if ($[0] !== t0) {
    ({
      editing,
      setEditing,
      className,
      editLabel,
      ...props
    } = t0);
    t1 = match(props.size).with("md", _temp).otherwise(_temp2);
    $[0] = t0;
    $[1] = className;
    $[2] = editLabel;
    $[3] = editing;
    $[4] = props;
    $[5] = setEditing;
    $[6] = t1;
  } else {
    className = $[1];
    editLabel = $[2];
    editing = $[3];
    props = $[4];
    setEditing = $[5];
    t1 = $[6];
  }
  const iconSize = t1;
  const t2 = editing ? "Done editing" : editLabel ?? "Edit description";
  let t3;
  if ($[7] !== className) {
    t3 = twMerge("", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== editing || $[10] !== setEditing) {
    t4 = () => setEditing(!editing);
    $[9] = editing;
    $[10] = setEditing;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== editing || $[13] !== iconSize) {
    t5 = editing ? <HiCheckCircle size={iconSize} /> : <MdEdit size={iconSize} />;
    $[12] = editing;
    $[13] = iconSize;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== props || $[16] !== t4 || $[17] !== t5) {
    t6 = <ActionIcon variant="subtle" onClick={t4} {...props}>{t5}</ActionIcon>;
    $[15] = props;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t3 || $[20] !== t6) {
    t7 = <div className={t3}>{t6}</div>;
    $[19] = t3;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t2 || $[23] !== t7) {
    t8 = <Tooltip zIndex={1000} label={t2}>{t7}</Tooltip>;
    $[22] = t2;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
const placeholderText = 'Notes, context and further clarifications...';
export const QuestionEditDescription = t0 => {
  const $ = _c(25);
  const {
    value,
    onChange,
    readonly,
    editing: editingRaw,
    setEditing,
    className
  } = t0;
  const editing = editingRaw ?? !readonly;
  let t1;
  if ($[0] !== value) {
    t1 = {
      description: value
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onChange) {
    t2 = t3 => {
      const {
        delta
      } = t3;
      return "description" in delta && onChange?.(delta.description);
    };
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      stored: t1,
      debounceSave: 800,
      onUpdate: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const localForm = useDbForm(t3);
  let t4;
  if ($[7] !== className) {
    t4 = twMerge("group relative", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== editing || $[10] !== localForm || $[11] !== readonly || $[12] !== value) {
    t5 = editing ? <RichText className="grow pr-2" value={localForm.values?.description ?? ""} syncValue={true} readonly={!editing} placeholder={placeholderText} onChange={v => localForm.setFieldValue("description", v)} /> : <Markdown className={twMerge(!readonly && isNullOrEmpty(value) ? "border-t border-b" : "", isNullOrEmpty(value) ? "opacity-30" : "")}>{isNullOrEmpty(value) ? placeholderText : value}</Markdown>;
    $[9] = editing;
    $[10] = localForm;
    $[11] = readonly;
    $[12] = value;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== editing || $[15] !== readonly || $[16] !== setEditing) {
    t6 = setEditing != null && !readonly && <EditActionIcon className={twMerge("absolute top-1 right-0 z-20", editing ? "opacity-100" : " group-hover:opacity-100 opacity-0")} editing={editing} setEditing={setEditing} />;
    $[14] = editing;
    $[15] = readonly;
    $[16] = setEditing;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== editing || $[19] !== t5 || $[20] !== t6) {
    t7 = <Expandable hideScroll={true} maxHeight="20em" showMoreOverlay={true} disabled={editing}>{t5}{t6}</Expandable>;
    $[18] = editing;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t4 || $[23] !== t7) {
    t8 = <div className={t4}>{t7}</div>;
    $[22] = t4;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};

/** Form that questions and sub-questions share (apart from the title, subtitle descriptions. that is placed separately to these details) */
export const QuestionCommonForm = ({
  form,
  topItems,
  readonly,
  isOwner,
  existingForecasts,
  simplified
}: {
  form: Pick<DbFormSubset<Pick<QuestionCfgCommon, 'title' | 'description' | 'defaultBounds' | 'hardBounds' | 'amountType' | 'unitLabel' | 'unitLabelLong' | 'numberFormat' | 'forecastType'>>, 'values' | 'setFieldValue' | 'setValues' | 'getInputProps'>;
  topItems?: ReactNode;
  existingForecasts?: number;
  isOwner?: boolean;
  readonly?: boolean;
  simplified?: boolean;
  showTitle?: boolean;
}) => {
  const {
    values
  } = form;
  const onDescChange = useCallback((md: string) => {
    form.setFieldValue('description', md);
  }, [form.setFieldValue]);

  // Add state to track local hard bounds changes
  const [pendingHardBounds, setPendingHardBounds] = useState<QuestionBounds | null>(null);
  const saveBounds = (change: Partial<Pick<QuestionCfg, 'defaultBounds' | 'hardBounds'>>) => {
    if (!values) return;
    form.setValues({
      ...pick(values, ['defaultBounds', 'hardBounds']),
      ...change,
      boundsVersion: '2'
    });
  };
  const qs = useQueryString(zQuestionQuery.pick({
    tab: true
  }));
  return values ? <>
        {!simplified && !readonly && topItems && <div className='flex justify-between items-center '>
            <div>{topItems}</div>
          </div>}
        {!simplified && <QuestionEditDescription value={values.description} onChange={onDescChange} readonly={readonly} />}

        {!readonly && values.forecastType == 'amount' && <div className='space-y-2' {...tourTipProps('new-question-amount', isOwner)}>
            <div className='flex gap-4 flex-wrap'>
              <AmountTypeCombo {...form.getInputProps('amountType')} hasExistingForecasts={!!existingForecasts} onChange={v => {
          form.setValues({
            amountType: v,
            defaultBounds: Bounds.defaultForType(v)
          }); // reset bounds when changing the type
        }} classes={{
          comboTarget: 'pt-1'
        }} withinPortal={false} />
              {values.amountType == 'number' && <>
                  {!simplified && <NumberFormat {...form.getInputProps('numberFormat')} />}
                  <TextInput {...form.getInputProps('unitLabel')} label={simplified ? 'Units' : 'Units (short)'} placeholder='e.g. km, ppl' className='w-[8em]' />
                  {!simplified && <TextInput {...form.getInputProps('unitLabelLong')} label='Units (long)' placeholder='e.g. kilometers, people' className='w-[14em]' />}
                </>}
            </div>
            <BoundsEdit simplified={simplified} value={{
        defaultBounds: values.defaultBounds,
        hardBounds: pendingHardBounds ?? values.hardBounds
      }} onChange={(v_0: {
        type: 'hard' | 'bound';
        value: number | undefined;
        name: 'min' | 'max';
      }) => {
        if (v_0.type === 'hard' && existingForecasts) {
          // Update pending state instead of form directly
          setPendingHardBounds(current => ({
            ...current,
            [v_0.name]: v_0.value
          }) as any);
        } else {
          // Handle as before for non-hard bounds or when no forecasts exist
          saveBounds({
            defaultBounds: compactObj({
              ...values?.defaultBounds,
              ...(v_0.type === 'bound' ? {
                [v_0.name]: v_0.value
              } : {})
            }),
            hardBounds: compactObj({
              ...values?.hardBounds,
              ...(v_0.type === 'hard' ? {
                [v_0.name]: v_0.value
              } : {})
            })
          });
        }
      }} format={Fmt.getOptions(values).absolute.long} />

            {/* Add warning button when pending changes exist */}
            {pendingHardBounds && existingForecasts && <Alert color='orange' icon={<HiExclamation />} title='Hard cut-off Change with existing forecasts' className='mb-4 max-w-[40em]'>
                <div>
                  Forecasts were made with{' '}
                  <b>
                    {values.hardBounds?.min != null ? `lower hard cut-off at ${values.hardBounds.min}` : 'no lower hard cut-off'}{' '}
                    {values.hardBounds?.min != null && values.hardBounds?.max != null ? 'and' : ''}{' '}
                    {values.hardBounds?.max != null ? `upper hard cut-off at ${values.hardBounds.max}` : 'no upper hard cut-off'}
                  </b>
                  . If you save the new hard bounds, consider if you need to ask for new forecasts. You can request new
                  forecasts by <b>Expiring</b> the related forecasts on the{' '}
                  <Link href={qs.getQueryUrl({
            tab: 'forecasts'
          })}>Forecasts tab</Link>.
                </div>
                <div className='flex justify-end'>
                  <Button className='mt-4 float-right mr-2' color='gray' onClick={() => setPendingHardBounds(null)}>
                    Cancel
                  </Button>
                  <Button className='mt-4' variant='filled' color='orange' onClick={() => {
            saveBounds({
              hardBounds: pendingHardBounds
            });
            setPendingHardBounds(null);
          }}>
                    Save new hard bounds
                  </Button>
                </div>
              </Alert>}
          </div>}
      </> : <></>;
};
const BoundsEdit = t0 => {
  const $ = _c(33);
  const {
    value,
    onChange,
    format,
    simplified
  } = t0;
  let defaultBounds;
  let hardBounds;
  let t1;
  if ($[0] !== value?.defaultBounds || $[1] !== value?.hardBounds) {
    defaultBounds = value?.defaultBounds ?? Bounds.defaultForType("number");
    let t2;
    if ($[5] !== value?.hardBounds) {
      t2 = value?.hardBounds ?? {};
      $[5] = value?.hardBounds;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    hardBounds = t2;
    const boundsTuple = Bounds.toTuple(defaultBounds);
    t1 = boundsTuple && Rnge.isDefined(boundsTuple) && !Rnge.isAscending(boundsTuple) ? "Upper needs to be above lower" : undefined;
    $[0] = value?.defaultBounds;
    $[1] = value?.hardBounds;
    $[2] = defaultBounds;
    $[3] = hardBounds;
    $[4] = t1;
  } else {
    defaultBounds = $[2];
    hardBounds = $[3];
    t1 = $[4];
  }
  const error = t1;
  let t2;
  if ($[7] !== defaultBounds || $[8] !== format || $[9] !== hardBounds || $[10] !== onChange || $[11] !== simplified) {
    t2 = name => ({
      name,
      value: {
        boundValue: defaultBounds[name],
        hardValue: hardBounds[name]
      },
      onChange: t3 => {
        const {
          type,
          value: value_0
        } = t3;
        return onChange?.({
          type,
          value: value_0,
          name
        });
      },
      format,
      simplified
    }) as BoundEditProps;
    $[7] = defaultBounds;
    $[8] = format;
    $[9] = hardBounds;
    $[10] = onChange;
    $[11] = simplified;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const inputProps = t2;
  let t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>The <b>display range</b> is the default zoom for new forecast and the display of the results.</div>;
    t4 = <b>Hard cut-off</b>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  const t5 = !simplified && " It is important to set hard cut-off before forecasts are made";
  let t6;
  if ($[15] !== t5) {
    t6 = <InfoLabel><div className="max-w-[50em]">{t3}<div>{t4} should be set when values outside the range are not possible (e.g. less than 0 deaths).{t5}</div></div></InfoLabel>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== inputProps) {
    t7 = inputProps("min");
    $[17] = inputProps;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== error || $[20] !== t7) {
    t8 = <BoundEdit {...t7} error={error} />;
    $[19] = error;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== inputProps) {
    t9 = inputProps("max");
    $[22] = inputProps;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== error || $[25] !== t9) {
    t10 = <BoundEdit {...t9} error={error} />;
    $[24] = error;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t8) {
    t11 = <div className=" mt-2 flex gap-4">{t8}{t10}</div>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t6) {
    t12 = <div className="py-4">{t6}{t11}</div>;
    $[30] = t11;
    $[31] = t6;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
};
const BoundInputControl = ({
  value,
  onChange,
  format,
  commonProps,
  inline
}: {
  value: number;
  onChange: (v: number | null) => void;
  format?: Fmt.Option;
  commonProps: {
    className: string;
    label: string;
    error?: string;
  };
  inline?: boolean;
}) => {
  return match(format?.type ?? 'number').with('number', () => <StringNumberInput {...commonProps} classNames={{
    section: 'opacity-50 text-sm px-1 font-semibold'
  }} rightSectionWidth={'auto'} value={value} onFocus={e => e.target.select()} onChange={v => {
    if (v != null) onChange(v);
  }} fmt={v => Fmt.fmt(v, format ? omit(format, ['unit']) : undefined)} rightSection={compactString(format?.unit)} />).with('date', () => {
    const dateInputProps = {
      ...commonProps,
      value: value,
      onChange: (v: number) => {
        if (v != null) onChange(v);
      }
    };
    return value && <DateInputMillis {...dateInputProps} inline={inline} />;
  }).exhaustive();
};
type BoundEditValue = {
  boundValue: number;
  hardValue?: number;
};
type BoundEditProps = {
  name: 'min' | 'max';
  value: BoundEditValue;
  // defaultValue?: BoundEditValue
  onChange?: (value: {
    type: 'bound' | 'hard';
    value: number | undefined;
  }) => void;
  error?: string;
  format?: Fmt.Option;
  inline?: boolean;
  hideHard?: boolean;
  simplified?: boolean;
};
const BoundEdit = t0 => {
  const $ = _c(24);
  const {
    name,
    value,
    onChange,
    error,
    format,
    inline,
    hideHard,
    simplified
  } = t0;
  const {
    boundValue,
    hardValue
  } = value;
  let t1;
  if ($[0] !== onChange) {
    t1 = v => v != null && onChange?.({
      type: "bound",
      value: v
    });
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = name == "max" ? "Upper display range" : "Lower display range";
  let t3;
  if ($[2] !== error || $[3] !== t2) {
    t3 = {
      className: "w-full",
      label: t2,
      error
    };
    $[2] = error;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== boundValue || $[6] !== format || $[7] !== inline || $[8] !== t1 || $[9] !== t3) {
    t4 = <BoundInputControl value={boundValue} onChange={t1} format={format} commonProps={t3} inline={inline} />;
    $[5] = boundValue;
    $[6] = format;
    $[7] = inline;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== boundValue || $[12] !== error || $[13] !== format || $[14] !== hardValue || $[15] !== hideHard || $[16] !== inline || $[17] !== name || $[18] !== onChange || $[19] !== simplified) {
    t5 = !hideHard && <><Switch label="hard cut-off" checked={hardValue != null} onChange={e => {
        onChange?.({
          type: "hard",
          value: e.currentTarget.checked ? boundValue : undefined
        });
      }} />{!simplified && hardValue != null && <BoundInputControl value={hardValue} onChange={v_0 => v_0 != null && onChange?.({
        type: "hard",
        value: v_0
      })} format={format} commonProps={{
        className: "w-full",
        label: name == "max" ? "Upper hard cut-off" : "Lower hard cut-off",
        error
      }} inline={inline} />}</>;
    $[11] = boundValue;
    $[12] = error;
    $[13] = format;
    $[14] = hardValue;
    $[15] = hideHard;
    $[16] = inline;
    $[17] = name;
    $[18] = onChange;
    $[19] = simplified;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = <div className="space-y-3">{t4}{t5}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
};
const NumberFormat = props => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div>A format pattern to control how numbers are formatted.</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex gap-2">Number format<InfoTip position="right-start" withArrow={true} offset={40} label={<div className="space-y-2 relative overflow-y-auto max-h-[80vh]">{t0}<div className="grid grid-cols-3 space-x-6 space-y-2"><b>Number</b><b>Number Format</b><b>Output</b>{numberFormatTable.map(_temp3)}</div></div>} /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <TextInput {...props} label={t1} placeholder="custom format e.g. #,#" className="w-[12em]" />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const ForecastTypeSection = t0 => {
  const $ = _c(6);
  const {
    readonly,
    inputProps
  } = t0;
  let t1;
  if ($[0] !== inputProps || $[1] !== readonly) {
    t1 = <ForecastTypeInput readonly={readonly} inputProps={inputProps} />;
    $[0] = inputProps;
    $[1] = readonly;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <InfoLabel className="max-w-sm">Once a forecast has been made, you will not be able to change the question type</InfoLabel>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <div className="mt-4 flex flex-wrap items-center gap-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
type ForecastTypeInputProps = Omit<SegmentedControlProps, 'data'>;
export const ForecastTypeInput = t0 => {
  const $ = _c(4);
  const {
    readonly,
    inputProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = entries(questionForecastMd).map(_temp4);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== inputProps || $[2] !== readonly) {
    t2 = <SegmentedControl {...inputProps} disabled={readonly} data={t1} />;
    $[1] = inputProps;
    $[2] = readonly;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const AmountTypeCombo = t0 => {
  const $ = _c(12);
  const {
    value,
    onChange,
    readonly,
    hasExistingForecasts,
    classes,
    withinPortal
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const store = useCombobox(t1);
  if (!value) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <></>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const {
    label
  } = questionAmountTypeMd[value];
  let t2;
  if ($[2] !== classes?.comboTarget || $[3] !== classes?.root || $[4] !== hasExistingForecasts || $[5] !== label || $[6] !== onChange || $[7] !== readonly || $[8] !== store || $[9] !== value || $[10] !== withinPortal) {
    t2 = readonly ? <AmountTypeIcon value={value} /> : <Input.Wrapper label="Axis mode" className={classes?.root}><Combo store={store} onOptionSubmit={s => onChange(s as QuestionCfg['amountType'])} withinPortal={withinPortal}><Combo.Target><div className={`flex items-center cursor-pointer gap-1 ${classes?.comboTarget ?? ""}`} onClick={() => store.openDropdown()}><AmountTypeIcon value={value} /><div className="font-semibold">{upperFirst(label)}</div><HiSelector /></div></Combo.Target><Combo.Dropdown><Combo.Options>{mapEntries(questionAmountTypeMd, (t, t3) => {
              const {
                desc,
                label: label_0
              } = t3;
              return <Combo.Option key={t} value={t} className="!py-2 space-y-1"><div className="flex items-center gap-2 ">{t == value && <CheckIcon size="1em" color="gray" />}<AmountTypeIcon value={t} /><div className="font-semibold">{upperFirst(label_0)}</div></div><div className="text-gray-600">{desc}</div>{t != value && hasExistingForecasts && <Alert color="amber" title="Existing forecasts will be redundant" className="max-w-[40em]">Switching to {label_0} can make some existing forecasts redundant. If you switch back to{" "}{questionAmountTypeMd[value].label} the current forecasts will appear again</Alert>}</Combo.Option>;
            })}</Combo.Options></Combo.Dropdown></Combo></Input.Wrapper>;
    $[2] = classes?.comboTarget;
    $[3] = classes?.root;
    $[4] = hasExistingForecasts;
    $[5] = label;
    $[6] = onChange;
    $[7] = readonly;
    $[8] = store;
    $[9] = value;
    $[10] = withinPortal;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
const AmountTypeIcon = t0 => {
  const $ = _c(16);
  const {
    value
  } = t0;
  const {
    label,
    desc
  } = questionAmountTypeMd[value];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      size: "18px",
      className: "text-gray-400"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = upperFirst(label);
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <b>{t2}</b>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <br />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== desc || $[7] !== t3) {
    t5 = <div>{t3}{t4}{desc}</div>;
    $[6] = desc;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== value) {
    t6 = match(value).with("number", _temp5).with("date", _temp6).exhaustive();
    $[9] = value;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <div>{t6}</div>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <IconContext.Provider value={t1}><Tooltip withinPortal={true} label={t5}>{t7}</Tooltip></IconContext.Provider>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
function _temp() {
  return 16;
}
function _temp2() {
  return 14;
}
function _temp3(t0, i) {
  const [num, format, output, note] = t0;
  return <Fragment key={i}><div>{num}</div><div className="py-1 font-bold">{format}</div><div>{output}{note && <div className="max-w-[20em] italic opacity-60">{note}</div>}</div></Fragment>;
}
function _temp4(t0) {
  const [q, t1] = t0;
  const {
    label
  } = t1;
  return {
    label: label ?? q,
    value: q
  };
}
function _temp5() {
  return <TiSortNumerically />;
}
function _temp6() {
  return <HiCalendar />;
}